/**
 * Payment Page
 * Página para reativar assinatura após teste expirado
 * Acessível apenas para o admin criador da conta
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Warning as WarningIcon, AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { apiService } from '@services/api';
import { ROUTES } from '@/constants';
import logger from '@utils/logger';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import Logo from '../components/Logo';
import { useSnackbar } from '@components/SnackbarNotification';
import PaymentMethodSection from './PaymentMethodSection';

interface PaymentInfo {
  isExpired: boolean;
  estimatedMonthlyPrice: number;
  currency: string;
  daysRemaining: number;
  isCreatorAdmin: boolean;
  isFinancialResponsible?: boolean;
  canManagePayment?: boolean;
  subscriptionActive: boolean;
  /** true só se pode usar o app (assinatura ativa + cobrança em dia). Inadimplência → false. */
  canUseApp?: boolean;
}

interface PaymentRequest {
  _id: string;
  amount: number;
  currency: string;
  paymentMethod: 'boleto' | 'pix';
  boleto?: {
    barcode?: string;
    digitableLine?: string;
    dueDate?: string;
    pdfUrl?: string;
    /** Cobrança Inter: em geral ainda pode ser paga após o vencimento conforme regras do banco */
    mayPayAfterDue?: boolean;
    /** Cancelamento solicitado ao banco; aguardar webhook antes de gerar PIX */
    cancelPending?: boolean;
  };
  pix?: {
    qrCode?: string;
    qrCodeImage?: string;
    copyPaste?: string;
    expiresAt?: string;
    expired?: boolean;
  };
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  paidAt?: string;
  createdAt: string;
}


// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
const PaymentPage: React.FC = () => {
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [pendingPayment, setPendingPayment] = useState<PaymentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState<'boleto' | 'pix' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restrictedMessage, setRestrictedMessage] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [interPixExpirationSeconds, setInterPixExpirationSeconds] = useState<number | null>(null);
  const [pixCountdown, setPixCountdown] = useState(0);
  const [pixTotalSeconds, setPixTotalSeconds] = useState(120);
  const [pixStatus, setPixStatus] = useState<'waiting' | 'confirmed' | 'timeout'>('waiting');
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const loadPaymentInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRestrictedMessage(null);
      try {
        const hints = await apiService.billing.getHints();
        const sec = hints.data?.data?.interPixExpirationSeconds;
        if (typeof sec === 'number' && sec > 0) setInterPixExpirationSeconds(sec);
      } catch {
        // billing hints opcional
      }
      const response = await apiService.getPaymentInfo();
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        const canUseApp = data.canUseApp ?? (data.subscriptionActive === true);

        if (canUseApp) {
          navigate(ROUTES.APP);
          return;
        }

        const canManage = data.canManagePayment ?? data.isCreatorAdmin;
        if (!canManage) {
          setRestrictedMessage('Entre em contato com o administrador da conta ou o responsável financeiro para regularizar a assinatura.');
          return;
        }

        const needsSubscriptionPayment =
          data.isExpired === true ||
          (data.subscriptionActive === true && canUseApp === false);

        if (!needsSubscriptionPayment) {
          setError('O período de teste ainda não expirou.');
          return;
        }

        setPaymentInfo(data);
      } else {
        setError('Erro ao carregar informações de pagamento.');
      }
    } catch (error: unknown) {
      logger.error('Error loading payment info', error);
      const axiosErr = error as { response?: { status?: number } };
      if (axiosErr.response?.status === 401 || axiosErr.response?.status === 403) {
        navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(ROUTES.PAYMENT)}`);
      } else {
        setError(getFriendlyErrorMessage(error, 'Erro ao carregar informações de pagamento. Tente novamente.'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadPendingPayment = useCallback(async () => {
    try {
      const response = await apiService.getPendingPayment();
      if (response.data.success && response.data.data) {
        setPendingPayment(response.data.data);
      }
    } catch (error: unknown) {
      // Se não houver pagamento pendente, não é erro
      const axiosErr = error as { response?: { status?: number } };
      if (axiosErr.response?.status !== 404) {
        logger.debug('No pending payment found');
      }
    }
  }, []);

  useEffect(() => {
    loadPaymentInfo();
    loadPendingPayment();
  }, [loadPaymentInfo, loadPendingPayment]);

  const pixExpired = pendingPayment?.pix?.expired === true;
  const isPixWithId = pendingPayment?.paymentMethod === 'pix' && !!pendingPayment?._id && !pixExpired;

  const computePixRemainingSeconds = useCallback(() => {
    const expIso = pendingPayment?.pix?.expiresAt;
    if (expIso) {
      const sec = Math.ceil((new Date(expIso).getTime() - Date.now()) / 1000);
      return Math.max(0, sec);
    }
    if (interPixExpirationSeconds != null && interPixExpirationSeconds > 0) {
      return interPixExpirationSeconds;
    }
    return 120;
  }, [pendingPayment?.pix?.expiresAt, interPixExpirationSeconds]);

  useEffect(() => {
    if (isPixWithId) {
      const t = computePixRemainingSeconds();
      setPixTotalSeconds(Math.max(1, t));
      setPixCountdown(t);
      setPixStatus('waiting');
    }
  }, [isPixWithId, computePixRemainingSeconds]);

  useEffect(() => {
    if (!isPixWithId || pixStatus !== 'waiting') return;
    countdownRef.current = setInterval(() => {
      setPixCountdown((prev) => {
        const next = prev <= 1 ? 0 : prev - 1;
        if (next <= 0) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = null;
          setPixStatus('timeout');
        }
        return next;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = null;
    };
  }, [isPixWithId, pixStatus]);

  useEffect(() => {
    if (!isPixWithId || pixStatus !== 'waiting' || !pendingPayment?._id) return;
    const poll = async () => {
      try {
        const response = await apiService.getPaymentStatus(pendingPayment._id);
        if (response.data?.success && response.data?.data?.status === 'confirmed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = null;
          setPixStatus('confirmed');
          showSnackbar(
            'Pagamento confirmado! Recibo e NFSe ficam disponíveis em Financeiro > Minhas faturas.',
            'success'
          );
          setTimeout(() => navigate(`${ROUTES.APP_FINANCE}?tab=invoices`), 800);
        }
      } catch {
        // ignore
      }
    };
    poll();
    pollingRef.current = setInterval(poll, 5000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [isPixWithId, pixStatus, pendingPayment?._id, navigate, showSnackbar]);

  /** Enquanto o banco não confirma o cancelamento do boleto (troca por PIX). */
  useEffect(() => {
    if (pendingPayment?.paymentMethod !== 'boleto' || !pendingPayment.boleto?.cancelPending) return;
    const id = window.setInterval(() => {
      void loadPendingPayment();
    }, 4000);
    return () => clearInterval(id);
  }, [pendingPayment?.paymentMethod, pendingPayment?.boleto?.cancelPending, loadPendingPayment]);

  const formatHms = (totalSec: number) => {
    const s = Math.max(0, Math.floor(totalSec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
  };

  const handleGenerateBoleto = async () => {
    try {
      setError(null);
      setGenerating(true);
      setGeneratingType('boleto');
      const response = await apiService.generateBoleto();
      if (response.data.success && response.data.data) {
        // Recarrega do backend para garantir payload completo (boletoUrl/linha digitável/etc.)
        try {
          const pending = await apiService.getPendingPayment();
          if (pending.data?.success && pending.data?.data) {
            setPendingPayment(pending.data.data);
          } else {
            setPendingPayment(response.data.data);
          }
        } catch {
          setPendingPayment(response.data.data);
        }
        showSnackbar('Boleto gerado com sucesso!', 'success');
      } else {
        setError(getFriendlyErrorMessage({ response: { data: response.data } }, 'Erro ao gerar boleto'));
      }
    } catch (err: unknown) {
      logger.error('Error generating boleto', err);
      setError(getFriendlyErrorMessage(err, 'Erro ao gerar boleto. Tente novamente.'));
    } finally {
      setGenerating(false);
      setGeneratingType(null);
    }
  };

  const handleGeneratePix = async () => {
    try {
      setError(null);
      setGenerating(true);
      setGeneratingType('pix');
      const response = await apiService.generatePix();
      const waitCode = (response.data as { billingWaitCode?: string }).billingWaitCode;
      if (waitCode === 'BOLETO_CANCEL_SUBMITTED' || waitCode === 'BOLETO_CANCEL_PENDING') {
        showSnackbar(
          'Cancelamento do boleto enviado ao banco. Aguarde a confirmação e tente gerar o PIX novamente em instantes.',
          'info'
        );
        await loadPendingPayment();
        return;
      }
      if (response.data.success && response.data.data) {
        // Recarrega do backend para garantir payload completo (qrCode/copyPaste/etc.)
        try {
          const pending = await apiService.getPendingPayment();
          if (pending.data?.success && pending.data?.data) {
            setPendingPayment(pending.data.data);
          } else {
            setPendingPayment(response.data.data);
          }
        } catch {
          setPendingPayment(response.data.data);
        }
        showSnackbar('PIX gerado com sucesso!', 'success');
      } else {
        setError(getFriendlyErrorMessage({ response: { data: response.data } }, 'Erro ao gerar PIX'));
      }
    } catch (err: unknown) {
      logger.error('Error generating PIX', err);
      setError(getFriendlyErrorMessage(err, 'Erro ao gerar PIX. Tente novamente.'));
    } finally {
      setGenerating(false);
      setGeneratingType(null);
    }
  };

  const handleConfirmPayment = async () => {
    if (!pendingPayment) return;

    try {
      setConfirming(true);
      setError(null);

      const response = await apiService.confirmPayment(pendingPayment._id);

      if (response.data.success) {
        showSnackbar(
          'Pagamento confirmado! Recibo e NFSe ficam disponíveis em Financeiro > Minhas faturas.',
          'success'
        );
        setConfirmDialogOpen(false);
        navigate(`${ROUTES.APP_FINANCE}?tab=invoices`, { replace: true });
      } else {
        setError(getFriendlyErrorMessage({ response: { data: response.data } }, 'Erro ao confirmar pagamento.'));
      }
    } catch (error: unknown) {
      logger.error('Error confirming payment', error);
      setError(
        getFriendlyErrorMessage(error, 'Erro ao confirmar pagamento. Tente novamente.')
      );
    } finally {
      setConfirming(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar(`${label} copiado para a área de transferência!`, 'success');
    }).catch(() => {
      showSnackbar('Erro ao copiar', 'error');
    });
  };

  const openBoletoPdf = async () => {
    if (!pendingPayment?._id) return;
    try {
      const res = await apiService.getBoletoPdf(pendingPayment._id);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // liberar depois de um tempo para o browser abrir a aba
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: unknown) {
      logger.error('Error opening boleto PDF', err);
      showSnackbar(getFriendlyErrorMessage(err, 'Erro ao abrir boleto em PDF.'), 'error');
    }
  };

  const formatCurrency = (value: number, currency: string = 'BRL'): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)'
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2
      }}
    >
      <Dialog open={generating} aria-labelledby="preparing-payment-dialog-title" maxWidth="xs" fullWidth>
        <DialogTitle id="preparing-payment-dialog-title">
          {generatingType === 'pix' ? 'Preparando PIX' : 'Preparando boleto'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <CircularProgress />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {generatingType === 'pix'
                  ? 'Estamos gerando o PIX junto ao Banco Inter.'
                  : 'Estamos gerando o boleto junto ao Banco Inter.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Isso pode levar alguns segundos. Não feche esta página.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <Container maxWidth="md">
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
              py: 3,
              px: 3,
              textAlign: 'center'
            }}
          >
            <Logo height={40} variant="auto" color="default" sx={{ mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'primary.dark', fontWeight: 600 }}>
              Ativação de Assinatura
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {error && (
              <Alert 
                severity="error" 
                icon={<WarningIcon />}
                sx={{ mb: 3 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {restrictedMessage && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <WarningIcon sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Assinatura Pendente
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {restrictedMessage}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Apenas o administrador que criou a conta ou usuários marcados como responsável financeiro podem regularizar o pagamento.
                </Typography>
              </Box>
            )}

            {paymentInfo && (
              <>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <WarningIcon 
                    sx={{ 
                      fontSize: 64, 
                      color: 'warning.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Pagamento Necessário
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentInfo.isExpired
                      ? 'Seu período de teste expirou. Gere um PIX ou boleto para ativar a assinatura e liberar o acesso.'
                      : 'Identificamos pendências de faturamento. Regularize o pagamento para liberar o acesso ao sistema.'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: 'primary.light',
                    borderRadius: 2,
                    p: 3,
                    mb: 4,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" color="primary.contrastText" display="block" gutterBottom>
                    Valor Mensal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <AttachMoneyIcon sx={{ color: 'primary.contrastText', fontSize: 32 }} />
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'primary.contrastText',
                        fontWeight: 700
                      }}
                    >
                      {formatCurrency(paymentInfo.estimatedMonthlyPrice, paymentInfo.currency)}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
                      /mês
                    </Typography>
                  </Box>
                </Box>

                <PaymentMethodSection
                  pendingPayment={pendingPayment}
                  generating={generating}
                  pixExpired={pixExpired}
                  isPixWithId={isPixWithId}
                  pixStatus={pixStatus}
                  pixCountdown={pixCountdown}
                  pixTotalSeconds={pixTotalSeconds}
                  formatHms={formatHms}
                  onGenerateBoleto={handleGenerateBoleto}
                  onGeneratePix={handleGeneratePix}
                  onOpenBoletoPdf={openBoletoPdf}
                  onCopyToClipboard={copyToClipboard}
                  onConfirmPaymentClick={() => {
                    setConfirmDialogOpen(true);
                    void handleConfirmPayment();
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Confirmação: apenas loading com etapas claras (sem campo de motivo) */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !confirming && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verificando pagamento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2 }}>
            {confirming ? <CircularProgress /> : null}
            <Typography variant="body1" fontWeight={600} align="center">
              {confirming
                ? 'Validando pagamento com a VOVER e com o banco…'
                : error || 'Não foi possível concluir a verificação.'}
            </Typography>
            {confirming && (
              <Typography variant="body2" color="text.secondary" align="center">
                Primeiro consultamos a VOVER; em seguida, quando aplicável, o Banco Inter. Não feche esta janela.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setError(null);
            }}
            disabled={confirming}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PaymentPage;
